import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';

/**
 * A single entry on the satellite/index timeline.
 * `date` is the canonical ISO key (`YYYY-MM-DD`) used for selection and map loading.
 */
export interface SatelliteTimelineDate {
  /** Canonical ISO key `YYYY-MM-DD`. */
  date: string;
  /** Optional pre-formatted label. When omitted it is derived from `date`. */
  label?: string;
  /** Whether to show the cloud icon (satellite products with cloud cover). */
  hasClouds?: boolean;
  /** Satellite product id (only present in satellite mode, used to load a single image). */
  productId?: string;
  /** Original payload (product or raw value) for the consumer. */
  raw?: unknown;
}

/**
 * Presentational horizontal date timeline (new map design).
 *
 * - Renders date chips with chevron navigation and optional cloud icons.
 * - Supports range selection (first click = start, second click = end).
 * - Emits {@link reachStart} when the user scrolls to the left edge so the parent
 *   can lazy-load the previous year.
 */
@Component({
  selector: 'app-satellite-timeline',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './satellite-timeline.component.html',
  styleUrls: ['./satellite-timeline.component.scss'],
})
export class SatelliteTimelineComponent implements OnChanges {
  @Input() dates: SatelliteTimelineDate[] = [];
  @Input() rangeStart: string | null = null;
  @Input() rangeEnd: string | null = null;
  @Input() loading = false;
  /** Active language (drives the localized chip label when no explicit label is provided). */
  @Input() lang = 'en-US';
  /** Show the fixed "All" chip (satellite mosaic mode). */
  @Input() showAllButton = false;
  /** Whether mosaic / all mode is currently active. */
  @Input() allModeActive = false;

  @Output() dateSelect = new EventEmitter<string>();
  @Output() allSelect = new EventEmitter<void>();
  @Output() reachStart = new EventEmitter<void>();
  @Output() reachEnd = new EventEmitter<void>();

  @ViewChild('strip', {static: false}) strip?: ElementRef<HTMLDivElement>;

  private readonly edgeThresholdPx = 48;
  private readonly scrollStepPx = 220;
  private edgeDetectionEnabled = false;
  private reachStartLocked = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['dates']) {
      return;
    }

    const previousDates: SatelliteTimelineDate[] = changes['dates'].previousValue ?? [];
    const previousLength = previousDates.length;
    const previousFirst = previousDates[0]?.date;
    const newFirst = this.dates[0]?.date;
    const prependedOlderYear = !!(
      previousLength > 0
      && previousFirst
      && newFirst
      && newFirst < previousFirst
    );
    const strip = this.strip?.nativeElement;
    const previousScrollWidth = strip?.scrollWidth ?? 0;
    const previousScrollLeft = strip?.scrollLeft ?? 0;

    queueMicrotask(() => {
      if (!this.dates.length) {
        return;
      }
      if (prependedOlderYear) {
        this.preserveScrollAfterPrepend(previousScrollWidth, previousScrollLeft);
        return;
      }
      if (previousLength === 0) {
        this.scrollToLatest();
      }
    });
  }

  isInRange(date: string): boolean {
    if (!this.rangeStart) {
      return false;
    }
    const end = this.rangeEnd ?? this.rangeStart;
    const min = this.rangeStart <= end ? this.rangeStart : end;
    const max = this.rangeStart <= end ? end : this.rangeStart;
    return date >= min && date <= max;
  }

  isRangeStart(date: string): boolean {
    if (!this.rangeStart) {
      return false;
    }
    const end = this.rangeEnd ?? this.rangeStart;
    const min = this.rangeStart <= end ? this.rangeStart : end;
    return date === min;
  }

  isRangeEnd(date: string): boolean {
    if (!this.rangeStart) {
      return false;
    }
    const end = this.rangeEnd ?? this.rangeStart;
    const max = this.rangeStart <= end ? end : this.rangeStart;
    return date === max;
  }

  onChipClick(entry: SatelliteTimelineDate): void {
    this.dateSelect.emit(entry.date);
  }

  onAllClick(): void {
    this.allSelect.emit();
  }

  formatLabel(entry: SatelliteTimelineDate): string {
    if (entry.label) {
      return entry.label;
    }
    const parsed = this.parseDate(entry.date);
    if (!parsed) {
      return entry.date;
    }
    try {
      return new Intl.DateTimeFormat(this.lang, {
        day: 'numeric',
        month: 'short',
        year: '2-digit',
      }).format(parsed);
    } catch {
      return entry.date;
    }
  }

  trackByDate(_index: number, entry: SatelliteTimelineDate): string {
    return entry.date;
  }

  /** Groups consecutive dates into outside / in-range segments for the range track UI. */
  get stripGroups(): Array<{type: 'outside' | 'range'; entries: SatelliteTimelineDate[]}> {
    const groups: Array<{type: 'outside' | 'range'; entries: SatelliteTimelineDate[]}> = [];
    for (const entry of this.dates) {
      const type: 'outside' | 'range' = this.isInRange(entry.date) ? 'range' : 'outside';
      const last = groups[groups.length - 1];
      if (last?.type === type) {
        last.entries.push(entry);
      } else {
        groups.push({type, entries: [entry]});
      }
    }
    return groups;
  }

  scrollPrev(): void {
    this.scrollBy(-this.scrollStepPx);
  }

  scrollNext(): void {
    this.scrollBy(this.scrollStepPx);
  }

  onStripScroll(): void {
    if (!this.edgeDetectionEnabled) {
      return;
    }
    this.checkEdges();
  }

  /** Scroll the strip to the latest date — only used on the first current-year load. */
  private scrollToLatest(): void {
    const el = this.strip?.nativeElement;
    if (!el || this.dates.length === 0) {
      return;
    }

    const latest = this.dates[this.dates.length - 1]?.date;
    this.edgeDetectionEnabled = false;

    requestAnimationFrame(() => {
      const chip = latest
        ? el.querySelector(`[data-date="${latest}"]`) as HTMLElement | null
        : null;
      if (chip) {
        chip.scrollIntoView({inline: 'end', block: 'nearest', behavior: 'auto'});
      } else {
        el.scrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      }

      window.setTimeout(() => {
        this.edgeDetectionEnabled = true;
      }, 400);
    });
  }

  private preserveScrollAfterPrepend(previousScrollWidth: number, previousScrollLeft: number): void {
    const el = this.strip?.nativeElement;
    if (!el) {
      return;
    }
    this.edgeDetectionEnabled = false;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const widthDelta = el.scrollWidth - previousScrollWidth;
        el.scrollLeft = previousScrollLeft + Math.max(0, widthDelta);
        window.setTimeout(() => {
          this.edgeDetectionEnabled = true;
        }, 200);
      });
    });
  }

  private scrollBy(delta: number): void {
    const el = this.strip?.nativeElement;
    if (!el) {
      return;
    }
    el.scrollBy({left: delta, behavior: 'smooth'});
  }

  private checkEdges(): void {
    const el = this.strip?.nativeElement;
    if (!el) {
      return;
    }
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) {
      return;
    }
    if (el.scrollLeft <= this.edgeThresholdPx) {
      if (this.reachStartLocked) {
        return;
      }
      this.reachStartLocked = true;
      this.reachStart.emit();
      window.setTimeout(() => {
        this.reachStartLocked = false;
      }, 800);
    }
  }

  private parseDate(iso: string): Date | null {
    if (!iso) {
      return null;
    }
    const parts = iso.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) {
      const fallback = new Date(iso);
      return Number.isNaN(fallback.getTime()) ? null : fallback;
    }
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }
}
